import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import {
  Mic,
  Handshake,
  Globe,
  Trophy,
  ExternalLink,
  Image,
} from 'lucide-react';

interface Achievement {
  id: string;
  title: string;
  category: string;
  date: string;
  description: string;
  image: string; // public 경로. 파일이 없으면 자동으로 placeholder 표시
  link?: string;
  icon: React.ReactNode;
}

/**
 * 사진은 public/achievements/ 에 아래 파일명으로 넣으면 자동 반영됩니다.
 * 파일이 없으면 임시 placeholder가 표시됩니다.
 */
const achievements: Achievement[] = [
  {
    id: 'kaist-cogneuro',
    title: '2025년 2학기 KAIST 인지신경과학 수업 SOCRATEST 시범 도입',
    category: '시범 도입',
    date: '2025년 2학기',
    description:
      'KAIST 인지신경과학 정규 강의에 SOCRATEST AI 구술 시험 플랫폼을 시범 도입했습니다. 실제 대학 강의 환경에서 AI 기반 구술·서술 평가와 부정행위 방지 시스템의 효용성을 검증한 첫 현장 적용 사례입니다.',
    image: '/achievements/kaist-cogneuro.jpg',
    icon: <Mic />,
  },
  {
    id: 'dongban-fair',
    title: '2026 상반기 KAIST 창업인 동반성장 Fair',
    category: '창업 행사',
    date: '2026 상반기',
    description:
      'KAIST가 주관하는 창업인 동반성장 Fair에 참가하여 FreakIT의 EdTech 솔루션을 소개하고, 선배 창업가 및 투자 생태계와 교류했습니다.',
    image: '/achievements/dongban-fair.jpg',
    link: 'https://startup.kaist.ac.kr/boards/view/board_event/2082',
    icon: <Handshake />,
  },
  {
    id: 'forum',
    title: '2026 혁신창업국가 대한민국 국제포럼',
    category: '국제 포럼',
    date: '2026',
    description:
      '서울대학교에서 개최된 「혁신창업국가 대한민국 국제포럼」에 참여하여 글로벌 창업·기술 혁신 흐름을 공유하고 FreakIT의 비전을 알렸습니다.',
    image: '/achievements/forum.jpg',
    link: 'https://www.snu.ac.kr/snunow/press?md=v&bbsidx=171629',
    icon: <Globe />,
  },
  {
    id: 'gravity',
    title: '4대 과기원 딥테크 학생 창업 통합리그 GRAVITY',
    category: '창업 대회',
    date: '2026',
    description:
      'KAIST·GIST·DGIST·UNIST 4대 과학기술원이 함께하는 딥테크 학생 창업 통합리그 GRAVITY에 참가하여 FreakIT의 기술력과 사업성을 겨뤘습니다.',
    image: '/achievements/gravity.jpg',
    link: 'https://www.kaist.ac.kr/news/html/news/?mode=V&mng_no=61970&skey=keyword&sval=%EC%9B%90&list_s_date=&list_e_date=&GotoPage=2',
    icon: <Trophy />,
  },
];

/** 이미지 로드 실패 시 placeholder로 대체하는 컴포넌트 */
const GalleryImage: React.FC<{ src: string; label: string; icon: React.ReactNode }> = ({
  src,
  label,
  icon,
}) => {
  const [errored, setErrored] = useState(false);

  if (errored || !src) {
    return (
      <Placeholder>
        <PlaceholderIcon>{icon}</PlaceholderIcon>
        <PlaceholderBadge>
          <Image /> 이미지 준비 중
        </PlaceholderBadge>
        <PlaceholderLabel>{label}</PlaceholderLabel>
      </Placeholder>
    );
  }

  return (
    <StyledImage
      src={src}
      alt={label}
      loading="lazy"
      onError={() => setErrored(true)}
    />
  );
};

const AchievementsPage: React.FC = () => {
  const [featured, ...rest] = achievements;

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroEyebrow>FreakIT NEWSROOM</HeroEyebrow>
          <HeroTitle>성과 & 활동</HeroTitle>
          <HeroDescription>
            FreakIT가 걸어온 길을 기록합니다. 실제 강의 현장 도입부터 창업 행사,
            국제 포럼, 딥테크 창업 리그까지 — 기술과 교육의 경계를 넓혀온 순간들입니다.
          </HeroDescription>
        </HeroContent>
      </HeroSection>

      <GallerySection>
        {/* ── 대표 성과 (Featured) ── */}
        <ScrollFadeIn direction="up" delay={0.1}>
          <FeaturedCard
            as={featured.link ? 'a' : 'div'}
            href={featured.link}
            target={featured.link ? '_blank' : undefined}
            rel={featured.link ? 'noopener noreferrer' : undefined}
            $clickable={!!featured.link}
          >
            <FeaturedImageWrap>
              <GalleryImage src={featured.image} label={featured.title} icon={featured.icon} />
              <CategoryBadge>{featured.category}</CategoryBadge>
            </FeaturedImageWrap>
            <FeaturedBody>
              <DateText>{featured.date}</DateText>
              <FeaturedTitle>{featured.title}</FeaturedTitle>
              <FeaturedDesc>{featured.description}</FeaturedDesc>
              {featured.link && (
                <ArticleLink>
                  기사 보기 <ExternalLink />
                </ArticleLink>
              )}
            </FeaturedBody>
          </FeaturedCard>
        </ScrollFadeIn>

        {/* ── 나머지 성과 그리드 ── */}
        <Grid>
          {rest.map((item, index) => (
            <ScrollFadeIn key={item.id} direction="up" delay={0.1 * (index + 1)}>
              <Card
                as={item.link ? 'a' : 'div'}
                href={item.link}
                target={item.link ? '_blank' : undefined}
                rel={item.link ? 'noopener noreferrer' : undefined}
                $clickable={!!item.link}
              >
                <ImageWrap>
                  <GalleryImage src={item.image} label={item.title} icon={item.icon} />
                  <CategoryBadge>{item.category}</CategoryBadge>
                </ImageWrap>
                <CardBody>
                  <DateText>{item.date}</DateText>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDesc>{item.description}</CardDesc>
                  {item.link && (
                    <ArticleLink>
                      기사 보기 <ExternalLink />
                    </ArticleLink>
                  )}
                </CardBody>
              </Card>
            </ScrollFadeIn>
          ))}
        </Grid>
      </GallerySection>
    </PageContainer>
  );
};

export default AchievementsPage;

/* ────────── Styled Components ────────── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  padding: 4.5rem 2rem 3rem;
  text-align: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const HeroContent = styled.div`
  max-width: 820px;
  margin: 0 auto;
  animation: ${fadeIn} 1s ease-out;
`;

const HeroEyebrow = styled.p`
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.25em;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 1.25rem;
`;

const HeroTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const GallerySection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3.5rem 2rem 5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const cardBase = `
  display: block;
  background: rgba(15, 23, 42, 0.25);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const FeaturedCard = styled.div<{ $clickable: boolean }>`
  ${cardBase}
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    transform: ${({ $clickable }) => ($clickable ? 'translateY(-8px)' : 'none')};
    box-shadow: ${({ $clickable }) => ($clickable ? '0 25px 50px rgba(0,0,0,0.5)' : 'none')};
    border-color: ${({ $clickable }) =>
      $clickable ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedImageWrap = styled.div`
  position: relative;
  min-height: 340px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: 240px;
  }
`;

const FeaturedBody = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 2rem;
  }
`;

const FeaturedTitle = styled.h2`
  font-size: 1.55rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.35;
  margin: 0.6rem 0 1.2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.5rem;
  }
`;

const FeaturedDesc = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(330px, 100%), 1fr));
  gap: 1.75rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $clickable: boolean }>`
  ${cardBase}
  display: flex;
  flex-direction: column;
  height: 100%;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    transform: ${({ $clickable }) => ($clickable ? 'translateY(-10px)' : 'none')};
    box-shadow: ${({ $clickable }) => ($clickable ? '0 20px 45px rgba(0,0,0,0.5)' : 'none')};
    border-color: ${({ $clickable }) =>
      $clickable ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'};
  }
`;

const ImageWrap = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
`;

const CardBody = styled.div`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.4;
  margin: 0.5rem 0 0.9rem;
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.25rem;
  flex: 1;
`;

const DateText = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent};
  letter-spacing: 0.02em;
`;

const CategoryBadge = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.4rem 0.95rem;
  background: rgba(2, 6, 23, 0.75);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #ffffff;
`;

const ArticleLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: gap 0.2s ease;

  svg {
    font-size: 0.8rem;
  }

  ${Card}:hover &, ${FeaturedCard}:hover & {
    gap: 0.85rem;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
  background: linear-gradient(135deg, #1e293b 0%, #020617 100%);
`;

const PlaceholderIcon = styled.div`
  font-size: 2.1rem;
  color: rgba(255, 255, 255, 0.5);
`;

const PlaceholderBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  padding: 0.3rem 0.8rem;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  border-radius: 100px;
`;

const PlaceholderLabel = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.45);
  max-width: 240px;
  line-height: 1.5;
`;
